/**
 * /project-executive — Project Executive Dashboard (sample)
 *
 * Single-pane view for a project executive: burn curve, doomsday
 * projection, WIP schedule, performance gauges (CPI / SPI / gross
 * margin), and AI-generated suggestions. All data is demo/sample.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_auth/project-executive")({
  component: ProjectExecutivePage,
});

// ── Project catalogue (sample) ─────────────────────────────────────────────────

type RiskLevel = "Low" | "Medium" | "Medium-High" | "High";

interface ProjectSummary {
  id: string;
  code: string;
  name: string;
  owner: string;
  projectMgr: string;
  superintendent: string;
  gcName: string;
  status: string;
  riskLevel: RiskLevel;
  startDate: string;
  plannedEnd: string;
  projectedEnd: string;
  slipDays: number;
  percentComplete: number;
  originalContract: number;
  approvedChanges: number;
  revisedContract: number;
  costToDate: number;
  committedCost: number;
  projectedFinalCost: number;
  contingencyRemaining: number;
  billedToDate: number;
  retainageHeld: number;
  cashReceived: number;
  cpi: number;
  spi: number;
  grossMargin: number;
}

const ALL_PROJECTS: ProjectSummary[] = [
  {
    id: "wctp2",
    code: "2025-041",
    name: "Wilshire Commercial Tower — Phase 2",
    owner: "Wilshire Properties LLC",
    projectMgr: "Sarah Chen, PMP",
    superintendent: "Mike Torres",
    gcName: "Bynaus Construction Group",
    status: "In Progress",
    riskLevel: "Medium-High",
    startDate: "Jan 6, 2025",
    plannedEnd: "Dec 31, 2025",
    projectedEnd: "Feb 14, 2026",
    slipDays: 45,
    percentComplete: 67,
    originalContract: 12_400_000,
    approvedChanges: 847_000,
    revisedContract: 13_247_000,
    costToDate: 8_910_000,
    committedCost: 11_560_000,
    projectedFinalCost: 13_890_000,
    contingencyRemaining: 310_000,
    billedToDate: 8_540_000,
    retainageHeld: 854_000,
    cashReceived: 7_200_000,
    cpi: 0.96,
    spi: 0.89,
    grossMargin: 4.8,
  },
  {
    id: "msd",
    code: "2024-027",
    name: "Marina Senior Living — Dietary & Kitchen",
    owner: "Harbor Health Group",
    projectMgr: "James Okafor",
    superintendent: "Lisa Park",
    gcName: "Bynaus Construction Group",
    status: "In Progress",
    riskLevel: "Low",
    startDate: "Sep 3, 2024",
    plannedEnd: "Mar 28, 2025",
    projectedEnd: "Apr 4, 2025",
    slipDays: 7,
    percentComplete: 89,
    originalContract: 3_200_000,
    approvedChanges: 115_000,
    revisedContract: 3_315_000,
    costToDate: 2_960_000,
    committedCost: 3_100_000,
    projectedFinalCost: 3_180_000,
    contingencyRemaining: 135_000,
    billedToDate: 2_980_000,
    retainageHeld: 298_000,
    cashReceived: 2_650_000,
    cpi: 1.04,
    spi: 0.98,
    grossMargin: 9.1,
  },
  {
    id: "dtpk",
    code: "2025-003",
    name: "Downtown Parking Structure — Seismic Retrofit",
    owner: "City of Los Angeles",
    projectMgr: "Aisha Reyes",
    superintendent: "Tom Nguyen",
    gcName: "Bynaus Construction Group",
    status: "In Progress",
    riskLevel: "High",
    startDate: "Feb 10, 2025",
    plannedEnd: "Nov 30, 2025",
    projectedEnd: "Feb 28, 2026",
    slipDays: 90,
    percentComplete: 31,
    originalContract: 8_750_000,
    approvedChanges: 1_240_000,
    revisedContract: 9_990_000,
    costToDate: 3_100_000,
    committedCost: 8_200_000,
    projectedFinalCost: 11_450_000,
    contingencyRemaining: 95_000,
    billedToDate: 2_800_000,
    retainageHeld: 280_000,
    cashReceived: 2_400_000,
    cpi: 0.89,
    spi: 0.79,
    grossMargin: -14.6,
  },
  {
    id: "sgw",
    code: "2025-018",
    name: "Sunset Gateway — Mixed-Use Podium",
    owner: "Pacific Rim Developers",
    projectMgr: "Sarah Chen, PMP",
    superintendent: "Rafael Dominguez",
    gcName: "Bynaus Construction Group",
    status: "Preconstruction",
    riskLevel: "Medium",
    startDate: "Jun 1, 2025",
    plannedEnd: "Dec 31, 2026",
    projectedEnd: "Dec 31, 2026",
    slipDays: 0,
    percentComplete: 4,
    originalContract: 22_500_000,
    approvedChanges: 0,
    revisedContract: 22_500_000,
    costToDate: 890_000,
    committedCost: 4_100_000,
    projectedFinalCost: 22_100_000,
    contingencyRemaining: 1_125_000,
    billedToDate: 850_000,
    retainageHeld: 85_000,
    cashReceived: 750_000,
    cpi: 1.01,
    spi: 1.02,
    grossMargin: 11.2,
  },
];

// ── Static chart data (tied to Wilshire project for sample purposes) ──────────

// Burn curve — 14 months (Jan 2025 = 0, Feb 2026 = 13)
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  "Jan'26", "Feb'26",
];
// currentMonthIdx = 7 (Aug 2025)
const CURRENT_MONTH = 7;

// Cumulative planned spend (S-curve), millions
const PLANNED = [
  0.40, 0.90, 1.60, 2.50, 3.60, 5.00,
  6.50, 7.80, 9.00, 10.40, 11.60, 12.80,
  null, null, // planned ends at Dec
];

// Cumulative actual spend through Aug
const ACTUAL = [
  0.35, 0.85, 1.55, 2.70, 3.90, 5.40,
  7.10, 8.91,
  null, null, null, null, null, null,
];

// Projected overrun curve (red dashed, from Aug → Feb'26)
const PROJECTED = [
  null, null, null, null, null, null,
  null, 8.91, 10.00, 11.00, 11.90, 12.70, 13.40, 13.89,
];

const BUDGET_LINE = ALL_PROJECTS[0].revisedContract / 1_000_000; // 13.247 — Wilshire sample

// WIP schedule rows
const WIP_ROWS = [
  { code: "01-1000", name: "General Conditions",       budget: 1_240_000, eac: 1_290_000, cost: 870_000,  pct: 68, billed: 835_000 },
  { code: "03-3000", name: "Concrete & Structural",    budget: 2_800_000, eac: 2_750_000, cost: 2_650_000, pct: 96, billed: 2_700_000 },
  { code: "05-1200", name: "Structural Steel",         budget: 1_950_000, eac: 1_980_000, cost: 1_820_000, pct: 92, billed: 1_750_000 },
  { code: "08-1100", name: "Doors, Frames & Hardware", budget: 480_000,   eac: 505_000,   cost: 320_000,  pct: 63, billed: 300_000 },
  { code: "09-9000", name: "Finishes",                 budget: 1_620_000, eac: 1_720_000, cost: 480_000,  pct: 28, billed: 420_000 },
  { code: "15-0000", name: "Mechanical / HVAC",        budget: 2_250_000, eac: 2_520_000, cost: 1_100_000, pct: 43, billed: 980_000 },
  { code: "16-0000", name: "Electrical",               budget: 2_120_000, eac: 2_190_000, cost: 1_670_000, pct: 76, billed: 1_555_000 },
  { code: "02-2000", name: "Site Work & Civil",        budget: 787_000,   eac: 935_000,   cost: 720_000,  pct: 77, billed: 700_000 },
];

// AI suggestions
const AI_SUGGESTIONS = [
  {
    id: 1,
    severity: "critical" as const,
    icon: "🔥",
    title: "MEP labor 14% over baseline — act now",
    body: "Mechanical & electrical labor productivity has trended 14% below baseline for 3 consecutive weeks. At this rate, MEP scope will overrun by $270K. Consider crew restructuring or accelerating prefab procurement to reduce field hours.",
    action: "View labor detail",
    href: "/payroll",
  },
  {
    id: 2,
    severity: "warning" as const,
    icon: "⚠️",
    title: "3 change orders pending owner execution — $127K at risk",
    body: "PCO-041, PCO-043, and PCO-047 totalling $127,400 have been submitted but not executed. Draw #8 closes in 11 days. Unsigned COs cannot be billed, impacting cash position by ~$114K net of retainage.",
    action: "View COs",
    href: "/change-orders",
  },
  {
    id: 3,
    severity: "warning" as const,
    icon: "📉",
    title: "SPI of 0.89 projects 45-day slip — critical path exposed",
    body: "Current schedule performance index suggests practical completion by Feb 14, 2026 — 45 days beyond contract. Liquidated damages clause triggers at $5,000/day after Jan 1. Expediting Finishes and MEP coordination could recover 20–25 days at ~$85K additional cost.",
    action: "Open schedule",
    href: "/job-cost",
  },
  {
    id: 4,
    severity: "info" as const,
    icon: "💡",
    title: "Retainage release opportunity — Concrete scope substantially complete",
    body: "Concrete & Structural is 96% complete with $270K retainage held. Requesting partial retainage release would improve cash position by approximately $240K net. Owner typically approves within 14 days when substantial completion affidavit is provided.",
    action: "Draft request",
    href: "/progress-billings",
  },
];

// buildPerf — derive performance metrics from a ProjectSummary
function buildPerf(p: ProjectSummary) {
  const earnedValue = p.revisedContract * (p.percentComplete / 100);
  const plannedValue = earnedValue / p.spi;
  return {
    cpi: p.cpi,
    spi: p.spi,
    grossMargin: p.grossMargin,
    earnedValue,
    plannedValue,
    budgetAtCompletion: p.revisedContract,
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2)}M`
    : `$${(n / 1_000).toFixed(0)}K`;

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

// ── Page ──────────────────────────────────────────────────────────────────────

function ProjectExecutivePage() {
  const [activeTab, setActiveTab] = useState<"overview" | "wip" | "cashflow">("overview");
  const [proj, setProj] = useState<ProjectSummary>(ALL_PROJECTS[0]);

  const overrun = proj.projectedFinalCost - proj.revisedContract;
  const perf = buildPerf(proj);
  const isNonDefault = proj.id !== ALL_PROJECTS[0].id;

  const openChat = (prompt: string) =>
    window.dispatchEvent(new CustomEvent("bynaus:open-agent", { detail: { prompt, autoSend: true } }));

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden bg-slate-50 dark:bg-gray-950">

      {/* ── Project selector bar ── */}
      <div className="flex-shrink-0 bg-slate-900 border-b border-slate-700 px-4 py-2 flex items-center gap-3 overflow-x-auto">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex-shrink-0">
          Project
        </span>
        {ALL_PROJECTS.map((p) => {
          const isActive = p.id === proj.id;
          const riskColor: Record<RiskLevel, string> = {
            Low: "#10b981", Medium: "#3b82f6", "Medium-High": "#f59e0b", High: "#ef4444",
          };
          return (
            <button
              key={p.id}
              onClick={() => setProj(p)}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-all ${
                isActive
                  ? "bg-white/10 border-white/25 text-white"
                  : "border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600"
              }`}
            >
              {/* Risk dot */}
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: riskColor[p.riskLevel] }}
              />
              <span className="hidden sm:inline">{p.code} — </span>
              <span className="truncate max-w-[160px]">{p.name.split(" — ")[0]}</span>
              {/* % bar */}
              <span className="hidden md:flex items-center gap-1 ml-1">
                <span className="text-[10px] text-slate-500">{p.percentComplete}%</span>
                <div className="w-10 h-1 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${p.percentComplete}%`,
                      background: riskColor[p.riskLevel],
                    }}
                  />
                </div>
              </span>
            </button>
          );
        })}
        <div className="ml-auto flex-shrink-0 text-[10px] text-slate-600">
          {ALL_PROJECTS.length} active projects
        </div>
      </div>

      {/* ── Project header ── */}
      <div
        className="flex-shrink-0 px-6 py-4 border-b border-slate-200 dark:border-gray-800"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[11px] font-medium text-slate-400 uppercase tracking-widest">
                Project Executive View
              </span>
              <RiskBadge level={proj.riskLevel} />
              <span className="text-[10px] text-slate-500 font-mono">{proj.code}</span>
            </div>
            <h1 className="text-xl font-bold text-white">{proj.name}</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {proj.owner} &nbsp;·&nbsp; {proj.gcName} &nbsp;·&nbsp;
              PM: {proj.projectMgr}
            </p>
          </div>
          {/* Key date pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <DatePill label="Contract start" value={proj.startDate} color="slate" />
            <DatePill label="Planned end" value={proj.plannedEnd} color="blue" />
            {proj.slipDays > 0
              ? <DatePill label="Projected end" value={proj.projectedEnd} color="red" sub={`+${proj.slipDays}d slip`} />
              : <DatePill label="Projected end" value={proj.projectedEnd} color="blue" sub="On schedule" />
            }
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("bynaus:open-agent", { detail: { prompt: `I'm reviewing the executive dashboard for ${proj.name} (project ${proj.code}). CPI is ${proj.cpi}, SPI is ${proj.spi}, we're ${proj.percentComplete}% complete. Give me a briefing on current project health, risks, and the top 3 actions I should take today.` } }))}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium border border-white/20 hover:bg-white/10 transition-colors"
            >
              <span>✦</span> Ask Bynaus
            </button>
          </div>
        </div>

        {/* ── KPI bar ── */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <KpiTile
            label="Revised Contract"
            value={fmt(proj.revisedContract)}
            sub={proj.approvedChanges > 0 ? `+${fmt(proj.approvedChanges)} in COs` : "No COs yet"}
            accent="blue"
          />
          <KpiTile
            label="Cost to Date"
            value={fmt(proj.costToDate)}
            sub={`${proj.percentComplete}% complete`}
            accent="blue"
          />
          <KpiTile
            label="Committed Cost"
            value={fmt(proj.committedCost)}
            sub="incl. subcontracts + POs"
            accent="blue"
          />
          <KpiTile
            label="Proj. Final Cost"
            value={fmt(proj.projectedFinalCost)}
            sub={`EAC — ${fmtPct(Math.abs(100 - (proj.projectedFinalCost / proj.revisedContract) * 100))} ${proj.projectedFinalCost > proj.revisedContract ? "over" : "margin"}`}
            accent={proj.projectedFinalCost > proj.revisedContract ? "red" : "amber"}
          />
          <KpiTile
            label={overrun >= 0 ? "Overrun" : "Savings"}
            value={`${overrun >= 0 ? "+" : "–"}${fmt(Math.abs(overrun))}`}
            sub="vs revised contract"
            accent={overrun >= 0 ? "red" : "green"}
          />
          <KpiTile
            label="Contingency Left"
            value={fmt(proj.contingencyRemaining)}
            sub={`${fmtPct((proj.contingencyRemaining / (proj.revisedContract * 0.05)) * 100)} of original`}
            accent={proj.contingencyRemaining < proj.revisedContract * 0.03 ? "red" : "green"}
          />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-6">
        <div className="flex gap-0">
          {(["overview", "wip", "cashflow"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                activeTab === t
                  ? "border-[#4f46e5] text-[#4f46e5]"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              {t === "overview" ? "Overview" : t === "wip" ? "WIP Schedule" : "Cash Flow"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {activeTab === "overview" && (
          <OverviewTab onChat={openChat} proj={proj} overrun={overrun} perf={perf} isNonDefault={isNonDefault} />
        )}
        {activeTab === "wip" && <WipTab proj={proj} isNonDefault={isNonDefault} />}
        {activeTab === "cashflow" && <CashFlowTab proj={proj} isNonDefault={isNonDefault} />}
      </div>
    </div>
  );
}

// ── Overview tab ───────────────────────────────────────────────────────────────

type PerfData = ReturnType<typeof buildPerf>;

function OverviewTab({
  onChat, proj, overrun, perf, isNonDefault,
}: {
  onChat: (p: string) => void;
  proj: ProjectSummary;
  overrun: number;
  perf: PerfData;
  isNonDefault: boolean;
}) {
  return (
    <div className="space-y-5">
      {isNonDefault && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-[11px] text-blue-700 dark:text-blue-400">
          <span>ℹ️</span>
          <span>Burn curve, WIP schedule, and AI suggestions below show <strong>Wilshire sample data</strong> for illustration. Live data for <strong>{proj.name}</strong> would load here in production.</span>
        </div>
      )}
      {/* Row 1: Burn curve + Performance */}
      <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 320px" }}>
        <BurnCurveCard />
        <PerformanceCard onChat={onChat} proj={proj} perf={perf} overrun={overrun} />
      </div>

      {/* Row 2: Doomsday + AI suggestions */}
      <div className="grid gap-5" style={{ gridTemplateColumns: "340px 1fr" }}>
        <DoomsdayCard onChat={onChat} proj={proj} overrun={overrun} />
        <AiSuggestionsCard onChat={onChat} proj={proj} />
      </div>
    </div>
  );
}

// ── Burn Curve ─────────────────────────────────────────────────────────────────

function BurnCurveCard() {
  // SVG dimensions
  const W = 640, H = 260;
  const PAD = { top: 16, right: 24, bottom: 36, left: 54 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxY = 15; // millions (top of Y axis)
  const numMonths = MONTHS.length; // 14

  const xPos = (i: number) => PAD.left + (i / (numMonths - 1)) * chartW;
  const yPos = (v: number) => PAD.top + chartH - (v / maxY) * chartH;

  // Build polyline point strings
  const plannedPts = PLANNED
    .map((v, i) => (v !== null ? `${xPos(i)},${yPos(v)}` : null))
    .filter(Boolean).join(" ");

  const actualPts = ACTUAL
    .map((v, i) => (v !== null ? `${xPos(i)},${yPos(v)}` : null))
    .filter(Boolean).join(" ");

  const projectedPts = PROJECTED
    .map((v, i) => (v !== null ? `${xPos(i)},${yPos(v)}` : null))
    .filter(Boolean).join(" ");

  const budgetY = yPos(BUDGET_LINE);
  const todayX  = xPos(CURRENT_MONTH);

  // Y-axis ticks
  const yTicks = [0, 2, 4, 6, 8, 10, 12, 14];

  return (
    <Card title="Burn Curve" subtitle="Cumulative cost vs plan — through Aug 2025">
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 420 }}>
          {/* Grid lines */}
          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left} y1={yPos(t)} x2={PAD.left + chartW} y2={yPos(t)}
                stroke="#e2e8f0" strokeWidth={1}
              />
              <text x={PAD.left - 6} y={yPos(t) + 4} textAnchor="end" fontSize={10} fill="#94a3b8">
                ${t}M
              </text>
            </g>
          ))}

          {/* Budget ceiling */}
          <line
            x1={PAD.left} y1={budgetY} x2={PAD.left + chartW} y2={budgetY}
            stroke="#ef4444" strokeWidth={1.5} strokeDasharray="6,3"
          />
          <text x={PAD.left + chartW + 4} y={budgetY + 4} fontSize={9} fill="#ef4444" fontWeight={600}>
            Budget
          </text>

          {/* Today line */}
          <line
            x1={todayX} y1={PAD.top} x2={todayX} y2={PAD.top + chartH}
            stroke="#94a3b8" strokeWidth={1} strokeDasharray="4,3"
          />
          <text x={todayX} y={PAD.top - 4} textAnchor="middle" fontSize={9} fill="#64748b">
            Today
          </text>

          {/* Planned S-curve */}
          <polyline
            points={plannedPts}
            fill="none" stroke="#93c5fd" strokeWidth={2} strokeDasharray="5,3"
          />

          {/* Projected overrun */}
          <polyline
            points={projectedPts}
            fill="none" stroke="#f97316" strokeWidth={2} strokeDasharray="4,3"
          />

          {/* Actual spend */}
          <polyline
            points={actualPts}
            fill="none" stroke="#3b82f6" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
          />

          {/* Actual data dots */}
          {ACTUAL.map((v, i) =>
            v !== null ? (
              <circle
                key={i}
                cx={xPos(i)} cy={yPos(v)}
                r={3.5} fill="#3b82f6" stroke="white" strokeWidth={1.5}
              />
            ) : null
          )}

          {/* X-axis labels */}
          {MONTHS.map((m, i) => (
            <text
              key={i}
              x={xPos(i)} y={H - 6}
              textAnchor="middle" fontSize={9}
              fill={i === CURRENT_MONTH ? "#1e40af" : i > 11 ? "#f97316" : "#94a3b8"}
              fontWeight={i === CURRENT_MONTH ? 700 : 400}
            >
              {m}
            </text>
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 mt-1 flex-wrap">
        <LegendItem color="#3b82f6" dash={false} label="Actual spend" />
        <LegendItem color="#93c5fd" dash label="Planned (S-curve)" />
        <LegendItem color="#f97316" dash label="Projected overrun" />
        <LegendItem color="#ef4444" dash label="Budget ceiling" />
      </div>
    </Card>
  );
}

function LegendItem({ color, dash, label }: { color: string; dash: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
      <svg width={24} height={8}>
        <line
          x1={0} y1={4} x2={24} y2={4}
          stroke={color} strokeWidth={2}
          strokeDasharray={dash ? "4,2" : "0"}
        />
      </svg>
      {label}
    </div>
  );
}

// ── Performance card ──────────────────────────────────────────────────────────

function PerformanceCard({
  onChat, proj, perf, overrun,
}: {
  onChat: (p: string) => void;
  proj: ProjectSummary;
  perf: PerfData;
  overrun: number;
}) {
  const vacColor = overrun >= 0 ? "text-red-600" : "text-emerald-600";
  return (
    <Card title="Performance" subtitle="Earned value analysis">
      <div className="space-y-4">
        <GaugeRow
          label="Cost Performance Index"
          abbr="CPI"
          value={perf.cpi}
          max={1.2}
          good={1.0}
          warn={0.95}
          format={(v) => v.toFixed(2)}
          sub={`EV ${fmt(perf.earnedValue)} / AC ${fmt(proj.costToDate)}`}
        />
        <GaugeRow
          label="Schedule Performance"
          abbr="SPI"
          value={perf.spi}
          max={1.2}
          good={1.0}
          warn={0.92}
          format={(v) => v.toFixed(2)}
          sub={`EV ${fmt(perf.earnedValue)} / PV ${fmt(perf.plannedValue)}`}
        />
        <GaugeRow
          label="Gross Margin"
          abbr="GM"
          value={perf.grossMargin}
          max={15}
          good={6}
          warn={2}
          format={(v) => `${v.toFixed(1)}%`}
          sub={`Proj. ${fmt(Math.abs(proj.revisedContract - proj.projectedFinalCost))} ${overrun < 0 ? "profit" : "overrun"}`}
        />

        <div className="pt-3 border-t border-slate-100 dark:border-gray-800 space-y-1.5 text-[11px] text-slate-500 dark:text-slate-400">
          <div className="flex justify-between">
            <span>Budget at Completion (BAC)</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">{fmt(perf.budgetAtCompletion)}</span>
          </div>
          <div className="flex justify-between">
            <span>Estimate at Completion (EAC)</span>
            <span className={`font-medium ${overrun >= 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(proj.projectedFinalCost)}</span>
          </div>
          <div className="flex justify-between">
            <span>Variance at Completion (VAC)</span>
            <span className={`font-medium ${vacColor}`}>{overrun >= 0 ? "–" : "+"}{fmt(Math.abs(overrun))}</span>
          </div>
        </div>

        <button
          onClick={() => onChat(`Analyze the earned value metrics for ${proj.name}: CPI is ${perf.cpi}, SPI is ${perf.spi}, EAC is $${proj.projectedFinalCost.toLocaleString()}. What do these numbers tell me and what are the most important actions?`)}
          className="w-full mt-1 py-1.5 rounded-lg text-[12px] font-medium border border-[#4f46e5] text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-950 transition-colors"
        >
          ✦ Explain these metrics
        </button>
      </div>
    </Card>
  );
}

function GaugeRow({
  label, abbr, value, max, good, warn, format, sub,
}: {
  label: string; abbr: string; value: number; max: number;
  good: number; warn: number; format: (v: number) => string; sub: string;
}) {
  const pct = Math.min((value / max) * 100, 100);
  const color = value >= good ? "#16a34a" : value >= warn ? "#d97706" : "#dc2626";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[12px] font-medium text-slate-700 dark:text-slate-300">{label}</span>
        <span className="text-[13px] font-bold" style={{ color }}>{format(value)}</span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <div className="mt-0.5 flex items-center justify-between">
        <span className="text-[10px] text-slate-400">{sub}</span>
        <span className="text-[10px]" style={{ color }}>
          {abbr} {value >= good ? "✓ On track" : value >= warn ? "▲ Watch" : "✗ At risk"}
        </span>
      </div>
    </div>
  );
}

// ── Doomsday report ───────────────────────────────────────────────────────────

function DoomsdayCard({ onChat, proj, overrun }: { onChat: (p: string) => void; proj: ProjectSummary; overrun: number }) {
  const cashRunway = proj.cashReceived + (proj.billedToDate - proj.cashReceived) * 0.9;
  const monthlyBurn = proj.costToDate / Math.max(proj.percentComplete / 12, 1);
  const runwayDays = Math.round(((cashRunway - proj.costToDate) / monthlyBurn) * 30);

  const ldExposure = proj.slipDays > 0 ? proj.slipDays * 5_000 : 0;
  const risks = [
    ...(ldExposure > 0 ? [{ label: `LD exposure (${proj.slipDays}d × $5K/d)`, value: fmt(ldExposure), color: "red" }] : []),
    { label: "Retainage held (risk capital)", value: fmt(proj.retainageHeld), color: "amber" },
    ...(overrun > 0 ? [{ label: "Projected cost overrun", value: fmt(overrun), color: "red" }] : []),
  ];

  return (
    <Card title="Doomsday Report" subtitle="Worst-case scenario analysis">
      <div className="space-y-3">
        {/* Completion date risk */}
        <div
          className="rounded-lg px-3 py-2.5 border"
          style={{ background: "#fef2f2", borderColor: "#fecaca" }}
        >
          <div className="text-[10px] font-bold text-red-500 uppercase tracking-wide mb-0.5">
            Projected Completion
          </div>
          <div
            className="text-lg font-bold"
            style={{ color: proj.slipDays > 0 ? "#b91c1c" : "#15803d" }}
          >
            {proj.projectedEnd}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: proj.slipDays > 0 ? "#ef4444" : "#16a34a" }}>
            {proj.slipDays > 0
              ? `${proj.slipDays} days beyond contract`
              : "On schedule — no slip projected"}
          </div>
        </div>

        {/* Overrun / Savings */}
        <div
          className="rounded-lg px-3 py-2.5 border"
          style={overrun >= 0
            ? { background: "#fff7ed", borderColor: "#fed7aa" }
            : { background: "#f0fdf4", borderColor: "#bbf7d0" }}
        >
          <div
            className="text-[10px] font-bold uppercase tracking-wide mb-0.5"
            style={{ color: overrun >= 0 ? "#f97316" : "#16a34a" }}
          >
            {overrun >= 0 ? "Cost Overrun (Projected)" : "Cost Savings (Projected)"}
          </div>
          <div
            className="text-lg font-bold"
            style={{ color: overrun >= 0 ? "#c2410c" : "#15803d" }}
          >
            {overrun >= 0 ? "+" : "–"}{fmt(Math.abs(overrun))}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: overrun >= 0 ? "#f97316" : "#16a34a" }}>
            {fmtPct(Math.abs((overrun / proj.revisedContract) * 100))} {overrun >= 0 ? "over" : "under"} revised contract
          </div>
        </div>

        {/* Cash runway */}
        <div
          className="rounded-lg px-3 py-2.5 border"
          style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}
        >
          <div className="text-[10px] font-bold text-green-600 uppercase tracking-wide mb-0.5">
            Cash Position
          </div>
          <div className="text-[13px] font-bold text-green-800">
            {fmt(Math.abs(proj.cashReceived - proj.costToDate * 0.85))} {proj.cashReceived > proj.costToDate * 0.85 ? "net positive" : "net negative"}
          </div>
          <div className="text-[11px] text-green-600 mt-0.5">
            ~{Math.max(0, runwayDays)} days runway at current burn rate
          </div>
        </div>

        {/* Risk register */}
        <div className="space-y-1.5 pt-1">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Risk Register</div>
          {risks.length === 0 && (
            <div className="text-[11px] text-emerald-600">✓ No material risks identified</div>
          )}
          {risks.map((r) => (
            <div key={r.label} className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500 dark:text-slate-400">{r.label}</span>
              <span className="font-bold" style={{ color: r.color === "red" ? "#dc2626" : "#d97706" }}>
                {r.value}
              </span>
            </div>
          ))}
          {risks.length > 0 && (
            <div className="flex items-center justify-between text-[11px] border-t border-slate-100 dark:border-gray-800 pt-1 mt-1">
              <span className="font-semibold text-slate-700 dark:text-slate-300">Total Exposure</span>
              <span className="font-bold text-red-700">
                {fmt(risks.reduce((s, r) => {
                  const n = parseInt(r.value.replace(/[$KM,]/g, "")) * (r.value.includes("M") ? 1_000 : 1) * 1_000;
                  return s + (isNaN(n) ? 0 : n);
                }, 0))}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={() => onChat(`Run a doomsday scenario analysis for ${proj.name}. Current overrun projection is ${fmt(Math.abs(overrun))}, SPI is ${proj.spi}, we have ${proj.slipDays} days of schedule slip. What's the worst-case outcome and what can we do right now to mitigate it?`)}
          className="w-full py-1.5 rounded-lg text-[12px] font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 dark:bg-red-950 dark:text-red-400 dark:border-red-900 transition-colors"
        >
          ✦ Run full doomsday analysis
        </button>
      </div>
    </Card>
  );
}

// ── AI Suggestions ────────────────────────────────────────────────────────────

function AiSuggestionsCard({ onChat, proj }: { onChat: (p: string) => void; proj: ProjectSummary }) {
  const severityStyle: Record<string, { bg: string; border: string; dot: string }> = {
    critical: { bg: "#fef2f2", border: "#fecaca", dot: "#dc2626" },
    warning:  { bg: "#fffbeb", border: "#fde68a", dot: "#d97706" },
    info:     { bg: "#eff6ff", border: "#bfdbfe", dot: "#3b82f6" },
  };

  return (
    <Card
      title="AI Suggestions"
      subtitle="Powered by AI · updated now"
      action={
        <button
          onClick={() => onChat(`Give me a comprehensive executive briefing for ${proj.name}: summarize all current issues, risks, and recommended actions in priority order.`)}
          className="text-[11px] font-medium text-[#4f46e5] hover:underline flex items-center gap-1"
        >
          ✦ Full briefing
        </button>
      }
    >
      <div className="space-y-3">
        {AI_SUGGESTIONS.map((s) => {
          const style = severityStyle[s.severity];
          return (
            <div
              key={s.id}
              className="rounded-lg px-3 py-2.5 border"
              style={{ background: style.bg, borderColor: style.border }}
            >
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5">{s.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: style.dot }}
                    />
                    <span className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">
                      {s.title}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    {s.body}
                  </p>
                  <button
                    onClick={() => onChat(`Tell me more about this issue for ${proj.name}: ${s.title}. ${s.body} What specific steps should I take?`)}
                    className="mt-1.5 text-[11px] font-medium text-[#4f46e5] hover:underline"
                  >
                    ✦ Dig deeper →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── WIP Schedule tab ───────────────────────────────────────────────────────────

function WipTab({ proj: _proj, isNonDefault: _nd }: { proj: ProjectSummary; isNonDefault: boolean }) {
  const totals = WIP_ROWS.reduce(
    (acc, r) => {
      acc.budget += r.budget;
      acc.eac += r.eac;
      acc.cost += r.cost;
      acc.billed += r.billed;
      return acc;
    },
    { budget: 0, eac: 0, cost: 0, billed: 0 }
  );

  return (
    <Card title="WIP Schedule" subtitle="Work in Progress — cost vs billing position by CSI division">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" style={{ minWidth: 780 }}>
          <thead>
            <tr className="border-b border-slate-200 dark:border-gray-700">
              {[
                "Code", "Description", "Budget", "EAC", "Cost to Date",
                "% Comp", "Earned Value", "Billed to Date", "Over / (Under)",
              ].map((h) => (
                <th
                  key={h}
                  className="py-2 px-2 text-[10px] font-bold text-slate-400 uppercase tracking-wide text-right first:text-left"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
            {WIP_ROWS.map((r) => {
              const earned = r.eac * (r.pct / 100);
              const overUnder = r.billed - earned;
              const isOver = overUnder > 0;
              const isRisk = r.eac > r.budget * 1.03;
              return (
                <tr
                  key={r.code}
                  className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="py-2.5 px-2 font-mono text-[11px] text-slate-400">{r.code}</td>
                  <td className="py-2.5 px-2 text-slate-700 dark:text-slate-200 font-medium flex items-center gap-1.5">
                    {r.name}
                    {isRisk && (
                      <span className="px-1 py-0.5 text-[9px] font-bold rounded bg-red-100 text-red-600">
                        OVER EAC
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-2 text-right text-slate-600 dark:text-slate-300">{fmt(r.budget)}</td>
                  <td className={`py-2.5 px-2 text-right font-medium ${isRisk ? "text-red-600" : "text-slate-600 dark:text-slate-300"}`}>
                    {fmt(r.eac)}
                  </td>
                  <td className="py-2.5 px-2 text-right text-slate-600 dark:text-slate-300">{fmt(r.cost)}</td>
                  <td className="py-2.5 px-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-16 h-1.5 bg-slate-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${r.pct}%`,
                            background: r.pct >= 90 ? "#16a34a" : r.pct >= 60 ? "#3b82f6" : "#94a3b8",
                          }}
                        />
                      </div>
                      <span className="text-slate-600 dark:text-slate-300 w-8 text-right">{r.pct}%</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-right text-slate-600 dark:text-slate-300">{fmt(earned)}</td>
                  <td className="py-2.5 px-2 text-right text-slate-600 dark:text-slate-300">{fmt(r.billed)}</td>
                  <td className={`py-2.5 px-2 text-right font-semibold ${isOver ? "text-emerald-600" : "text-red-600"}`}>
                    {isOver ? "+" : "–"}{fmt(Math.abs(overUnder))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 dark:border-gray-600 bg-slate-50 dark:bg-gray-800/50">
              <td colSpan={2} className="py-2.5 px-2 font-bold text-slate-700 dark:text-slate-200 text-[12px]">
                Project Total
              </td>
              <td className="py-2.5 px-2 text-right font-bold text-slate-700 dark:text-slate-200">{fmt(totals.budget)}</td>
              <td className="py-2.5 px-2 text-right font-bold text-red-600">{fmt(totals.eac)}</td>
              <td className="py-2.5 px-2 text-right font-bold text-slate-700 dark:text-slate-200">{fmt(totals.cost)}</td>
              <td className="py-2.5 px-2 text-right font-bold text-slate-700 dark:text-slate-200">
                {fmtPct((totals.cost / totals.eac) * 100)}
              </td>
              <td className="py-2.5 px-2 text-right font-bold text-slate-700 dark:text-slate-200">
                {fmt(WIP_ROWS.reduce((s, r) => s + r.eac * (r.pct / 100), 0))}
              </td>
              <td className="py-2.5 px-2 text-right font-bold text-slate-700 dark:text-slate-200">{fmt(totals.billed)}</td>
              <td className="py-2.5 px-2 text-right font-bold text-red-600">
                {(() => {
                  const earned = WIP_ROWS.reduce((s, r) => s + r.eac * (r.pct / 100), 0);
                  const diff = totals.billed - earned;
                  return `${diff < 0 ? "–" : "+"}${fmt(Math.abs(diff))}`;
                })()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* WIP note */}
      <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-400">
        <strong>WIP Position:</strong> Project is in an underbilling position of approximately $335K — earned value
        exceeds billings. Recommend submitting Draw #8 promptly. Finishes and MEP are the primary drivers of underbilling;
        both have significant earned value not yet billed.
      </div>
    </Card>
  );
}

// ── Cash Flow tab ─────────────────────────────────────────────────────────────

function CashFlowTab({ proj: _proj, isNonDefault: _nd }: { proj: ProjectSummary; isNonDefault: boolean }) {
  const months = ["May", "Jun", "Jul", "Aug", "Sep*", "Oct*", "Nov*", "Dec*", "Jan'26*", "Feb'26*"];
  // Monthly cost (actual then projected)
  const monthlyCost = [1_200_000, 1_400_000, 1_700_000, 1_810_000, 1_090_000, 1_000_000, 900_000, 800_000, 700_000, 490_000];
  // Monthly billing
  const monthlyBilled = [1_100_000, 1_300_000, 1_500_000, 1_640_000, 1_200_000, 1_100_000, 950_000, 850_000, 720_000, 520_000];
  // Cash received (typically 30 days lag)
  const cashReceived = [980_000, 1_050_000, 1_280_000, 1_500_000, 1_400_000, 1_150_000, 1_080_000, 930_000, 840_000, 710_000];

  const maxVal = Math.max(...monthlyCost, ...monthlyBilled, ...cashReceived);
  const barH = 120;

  return (
    <div className="space-y-5">
      <Card title="Monthly Cash Flow" subtitle="Cost · Billing · Cash received — *projected">
        {/* Bar chart */}
        <div className="overflow-x-auto">
          <svg viewBox={`0 0 ${months.length * 72 + 40} ${barH + 60}`} className="w-full" style={{ minWidth: 600 }}>
            {months.map((m, i) => {
              const x = 20 + i * 72;
              const barW = 16;
              const gap = 4;
              const isProjected = m.includes("*");

              const h1 = (monthlyCost[i] / maxVal) * barH;
              const h2 = (monthlyBilled[i] / maxVal) * barH;
              const h3 = (cashReceived[i] / maxVal) * barH;

              return (
                <g key={i}>
                  {/* Cost bar */}
                  <rect x={x} y={barH - h1} width={barW} height={h1}
                    fill={isProjected ? "#fca5a5" : "#ef4444"} rx={2} opacity={isProjected ? 0.7 : 1} />
                  {/* Billed bar */}
                  <rect x={x + barW + gap} y={barH - h2} width={barW} height={h2}
                    fill={isProjected ? "#93c5fd" : "#3b82f6"} rx={2} opacity={isProjected ? 0.7 : 1} />
                  {/* Received bar */}
                  <rect x={x + (barW + gap) * 2} y={barH - h3} width={barW} height={h3}
                    fill={isProjected ? "#6ee7b7" : "#10b981"} rx={2} opacity={isProjected ? 0.7 : 1} />
                  {/* Month label */}
                  <text x={x + (barW + gap) * 1.5} y={barH + 16} textAnchor="middle" fontSize={10}
                    fill={isProjected ? "#94a3b8" : "#64748b"}>
                    {m}
                  </text>
                  {/* Cost label above */}
                  <text x={x + barW / 2} y={barH - h1 - 3} textAnchor="middle" fontSize={8} fill="#ef4444">
                    {(monthlyCost[i] / 1_000_000).toFixed(1)}
                  </text>
                </g>
              );
            })}
            {/* Baseline */}
            <line x1={20} y1={barH} x2={months.length * 72 + 20} y2={barH} stroke="#e2e8f0" strokeWidth={1} />
          </svg>
        </div>
        <div className="flex gap-5 mt-1">
          <LegendItem color="#ef4444" dash={false} label="Monthly cost" />
          <LegendItem color="#3b82f6" dash={false} label="Billed to owner" />
          <LegendItem color="#10b981" dash={false} label="Cash received" />
        </div>
      </Card>

      {/* Draw schedule */}
      <Card title="Draw Schedule" subtitle="AIA progress billing timeline">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" style={{ minWidth: 600 }}>
            <thead>
              <tr className="border-b border-slate-200 dark:border-gray-700">
                {["Draw #", "Submitted", "Amount", "Retainage", "Net Due", "Status", "Cash Received"].map((h) => (
                  <th key={h} className="py-2 px-3 text-[10px] font-bold text-slate-400 uppercase tracking-wide text-left">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {[
                { draw: 1, date: "Feb 1", amount: 480_000, status: "paid" },
                { draw: 2, date: "Mar 1", amount: 710_000, status: "paid" },
                { draw: 3, date: "Apr 1", amount: 1_050_000, status: "paid" },
                { draw: 4, date: "May 1", amount: 1_230_000, status: "paid" },
                { draw: 5, date: "Jun 1", amount: 1_380_000, status: "paid" },
                { draw: 6, date: "Jul 1", amount: 1_500_000, status: "paid" },
                { draw: 7, date: "Aug 1", amount: 1_640_000, status: "pending", note: "Pending owner approval" },
                { draw: 8, date: "Sep 15", amount: 1_200_000, status: "upcoming", note: "Opens Sep 1" },
              ].map((d) => {
                const ret = d.amount * 0.1;
                const net = d.amount - ret;
                const statusColor = d.status === "paid" ? "#16a34a" : d.status === "pending" ? "#d97706" : "#94a3b8";
                const statusLabel = d.status === "paid" ? "✓ Paid" : d.status === "pending" ? "⏳ Pending" : "📅 Upcoming";
                return (
                  <tr key={d.draw} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                    <td className="py-2.5 px-3 font-semibold text-slate-700 dark:text-slate-200">#{d.draw}</td>
                    <td className="py-2.5 px-3 text-slate-500">{d.date}, 2025</td>
                    <td className="py-2.5 px-3 font-medium text-slate-700 dark:text-slate-200">{fmt(d.amount)}</td>
                    <td className="py-2.5 px-3 text-slate-500">{fmt(ret)}</td>
                    <td className="py-2.5 px-3 font-medium text-slate-700 dark:text-slate-200">{fmt(net)}</td>
                    <td className="py-2.5 px-3">
                      <span className="text-[11px] font-semibold" style={{ color: statusColor }}>
                        {statusLabel}
                      </span>
                      {d.note && <div className="text-[10px] text-slate-400 mt-0.5">{d.note}</div>}
                    </td>
                    <td className="py-2.5 px-3 text-slate-500">
                      {d.status === "paid" ? fmt(net * 0.95) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Shared components ─────────────────────────────────────────────────────────

function Card({
  title, subtitle, children, action,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
          {subtitle && (
            <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function KpiTile({
  label, value, sub, accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent: "blue" | "amber" | "red" | "green" | "slate";
}) {
  const colors: Record<string, string> = {
    blue:  "#93c5fd",
    amber: "#fcd34d",
    red:   "#fca5a5",
    green: "#6ee7b7",
    slate: "#cbd5e1",
  };
  return (
    <div
      className="rounded-lg px-3 py-2 border"
      style={{ background: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.12)" }}
    >
      <div className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: colors[accent] }}>
        {label}
      </div>
      <div className="text-[15px] font-bold text-white leading-tight">{value}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}

function DatePill({
  label, value, color, sub,
}: {
  label: string;
  value: string;
  color: "slate" | "blue" | "red";
  sub?: string;
}) {
  const styles: Record<string, { bg: string; text: string; border: string }> = {
    slate: { bg: "rgba(255,255,255,0.07)", text: "#cbd5e1", border: "rgba(255,255,255,0.12)" },
    blue:  { bg: "rgba(59,130,246,0.15)", text: "#93c5fd", border: "rgba(59,130,246,0.3)" },
    red:   { bg: "rgba(239,68,68,0.15)",  text: "#fca5a5", border: "rgba(239,68,68,0.3)" },
  };
  const s = styles[color];
  return (
    <div
      className="rounded-lg px-3 py-1.5 text-center border"
      style={{ background: s.bg, borderColor: s.border }}
    >
      <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: s.text }}>
        {label}
      </div>
      <div className="text-[12px] font-semibold text-white mt-0.5">{value}</div>
      {sub && <div className="text-[9px] font-bold mt-0.5" style={{ color: s.text }}>{sub}</div>}
    </div>
  );
}

function RiskBadge({ level }: { level: string }) {
  const isHigh = level.includes("High");
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
      style={{
        background: isHigh ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)",
        color: isHigh ? "#fca5a5" : "#fcd34d",
        border: `1px solid ${isHigh ? "rgba(239,68,68,0.4)" : "rgba(245,158,11,0.4)"}`,
      }}
    >
      {level} Risk
    </span>
  );
}
